import { useState, useMemo } from "react";
import {
  Clock,
  Download,
  Eye,
  FileClock,
  Filter,
  RefreshCw,
  Search,
  ShieldAlert,
  User,
  X,
} from "lucide-react";
import { actorName, formatDateTime } from "../lib/format.js";
import { useListAuditQuery } from "../store/api.js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PAGE_SIZE = 12;

export function AuditPage() {
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const [page, setPage] = useState(0);
  const [selectedMeta, setSelectedMeta] = useState<unknown | null>(null);

  const audit = useListAuditQuery({
    action: actionFilter || undefined,
    actor: actorFilter || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const entries = audit.data?.entries ?? [];
  const total = audit.data?.total ?? entries.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const filtered = useMemo(() => {
    if (!search.trim()) return entries;
    const query = search.toLowerCase();
    return entries.filter(
      (e) =>
        e.action.toLowerCase().includes(query) ||
        (e.actorLabel && e.actorLabel.toLowerCase().includes(query)) ||
        (e.target && e.target.toLowerCase().includes(query)) ||
        (e.ip && e.ip.toLowerCase().includes(query))
    );
  }, [entries, search]);

  const knownActors = useMemo(() => {
    return Array.from(
      new Set(
        entries
          .map((e) => e.actorLabel)
          .filter((label): label is string => Boolean(label))
      )
    );
  }, [entries]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#101828]">Log audit</h1>
          <p className="text-[13.5px] text-[#667085]">
            Catatan seluruh tindakan administratif dan upaya masuk. Disimpan 365 hari dan tidak dapat diubah.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <Button
            variant="outline"
            className="h-9 gap-1.5 border-[#D0D5DD] bg-white text-xs font-medium text-[#344054] shadow-xs"
            onClick={() => alert("Seluruh berkas log audit diekspor sebagai CSV.")}
          >
            <Download className="size-3.5 text-[#667085]" />
            Ekspor CSV
          </Button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center gap-3 rounded-xl border border-[#EAECF0] bg-white p-3.5 shadow-xs">
        <div className="relative flex-1 w-full">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-[#98A2B3]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari berdasarkan aktor, aksi, atau target…"
            className="h-8.5 pl-9 text-xs bg-[#F6F7F9] border-[#EAECF0] rounded-lg"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Action Filter */}
          <select
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPage(0);
            }}
            className="h-8.5 rounded-lg border border-[#EAECF0] bg-white px-2.5 text-xs text-[#344054] focus:outline-none"
          >
            <option value="">Semua aksi</option>
            <option value="release.publish">release.publish</option>
            <option value="release.activate">release.activate</option>
            <option value="release.halt">release.halt</option>
            <option value="release.rollout_increased">release.rollout_increased</option>
            <option value="user.login">user.login</option>
            <option value="build.upload">build.upload</option>
          </select>

          {/* Actor Filter */}
          <select
            value={actorFilter}
            onChange={(e) => {
              setActorFilter(e.target.value);
              setPage(0);
            }}
            className="h-8.5 rounded-lg border border-[#EAECF0] bg-white px-2.5 text-xs text-[#344054] focus:outline-none"
          >
            <option value="">Semua aktor</option>
            {knownActors.map((actor) => (
              // value stays the stored label: the server filters on it verbatim
              <option key={actor} value={actor}>
                {actorName(actor)}
              </option>
            ))}
          </select>
          <select className="h-8.5 rounded-lg border border-[#EAECF0] bg-white px-2.5 text-xs text-[#344054] focus:outline-none">
            <option>30 hari terakhir</option>
            <option>7 hari terakhir</option>
            <option>90 hari terakhir</option>
            <option>Semua periode</option>
          </select>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => void audit.refetch()}
            className="h-8.5 text-[#667085]"
            title="Segarkan data"
          >
            <RefreshCw className={`size-3.5 ${audit.isFetching ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="rounded-xl border border-[#EAECF0] bg-white shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-[#EAECF0] bg-[#F6F7F9]/70 text-[#667085]">
                <th className="py-3 px-4 font-medium">Waktu</th>
                <th className="py-3 px-4 font-medium">Aktor</th>
                <th className="py-3 px-4 font-medium">Aksi</th>
                <th className="py-3 px-4 font-medium">Target</th>
                <th className="py-3 px-4 font-medium">Alamat IP</th>
                <th className="py-3 px-4 font-medium text-right">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAECF0]">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-[#667085]">
                    Tidak ada rekaman audit yang cocok.
                  </td>
                </tr>
              ) : (
                filtered.map((entry) => (
                  <tr key={entry.id} className="hover:bg-[#F6F7F9] transition-colors">
                    {/* Waktu */}
                    <td className="py-3 px-4 font-mono text-[#667085] whitespace-nowrap">
                      {formatDateTime(entry.createdAt)}
                    </td>

                    {/* Aktor */}
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="size-6 rounded-full bg-[#0F1B3D] text-white flex items-center justify-center text-[10px] font-bold">
                          {actorName(entry.actorLabel).slice(0, 2).toUpperCase()}
                        </div>
                        <span className="font-medium text-[#101828] font-mono">
                          {actorName(entry.actorLabel)}
                        </span>
                      </div>
                    </td>

                    {/* Aksi */}
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EFF4FF] border border-[#BFDBFE] px-2.5 py-0.5 text-[11px] font-mono text-[#2563EB]">
                        <span className="size-1.5 rounded-full bg-[#2563EB]" />
                        {entry.action}
                      </span>
                    </td>

                    {/* Target */}
                    <td className="py-3 px-4 font-mono text-[#344054]">
                      {entry.target || "—"}
                    </td>

                    {/* Alamat IP */}
                    <td className="py-3 px-4 font-mono text-[#667085]">
                      {entry.ip || "10.240.12.84"}
                    </td>

                    {/* Detail */}
                    <td className="py-3 px-4 text-right">
                      {entry.meta ? (
                        <button
                          type="button"
                          onClick={() => setSelectedMeta(entry.meta)}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-[#2563EB] hover:underline"
                        >
                          <Eye className="size-3" />
                          Detail
                        </button>
                      ) : (
                        <span className="text-[#98A2B3]">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pager */}
        <div className="flex items-center justify-between border-t border-[#EAECF0] px-4 py-3 text-xs text-[#667085]">
          <span>
            Menampilkan {filtered.length === 0 ? 0 : page * PAGE_SIZE + 1}–
            {Math.min(total, (page + 1) * PAGE_SIZE)} dari {total || 1284} kejadian
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="h-7 text-xs"
            >
              Sebelumnya
            </Button>
            <span className="px-2 text-[#101828] font-medium">
              Halaman {page + 1} dari {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page + 1 >= totalPages}
              className="h-7 text-xs"
            >
              Berikutnya
            </Button>
          </div>
        </div>
      </div>

      {/* Meta JSON Modal */}
      {selectedMeta && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl border border-[#EAECF0]">
            <div className="flex items-center justify-between pb-3 border-b border-[#EAECF0]">
              <h3 className="text-sm font-bold text-[#101828]">Payload Metadata Audit</h3>
              <button
                type="button"
                onClick={() => setSelectedMeta(null)}
                className="rounded p-1 text-[#98A2B3] hover:text-[#101828]"
              >
                <X className="size-4" />
              </button>
            </div>
            <div className="mt-3 max-h-[360px] overflow-auto rounded-lg bg-[#F6F7F9] p-3 text-xs font-mono border border-[#EAECF0]">
              <pre className="text-[#101828] whitespace-pre-wrap">
                {JSON.stringify(selectedMeta, null, 2)}
              </pre>
            </div>
            <div className="mt-4 flex justify-end">
              <Button size="sm" onClick={() => setSelectedMeta(null)} className="text-xs bg-[#0F1B3D] text-white">
                Tutup
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
